import { Injectable, signal, inject } from '@angular/core';
import { finalize, firstValueFrom, Observable, Subject } from 'rxjs';
import { LoadingPageService } from './loading-page.service';

export interface ConfirmAndExecuteResult<T> {
  confirmed: boolean;
  result?: T;
  error?: unknown;
}

export interface ConfirmOptions {
  title?: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  type?: 'Neutral' | 'Success' | 'Error' | 'Warning' | 'Info';
  keepOpenOnConfirm?: boolean;
}

export const dialogCloseOptions: ConfirmOptions = {
  title: 'Unsaved Changes',
  message:
    'You have edited fields. If you have made any modifications, they will be lost. Do you wish to leave anyway?',
  confirmLabel: 'Discard and Close',
  cancelLabel: 'Keep Editing',
  type: 'Warning'
};

export const pageCloseOptions: ConfirmOptions = {
  title: 'Unsaved Changes',
  message:
    'You have edited fields. If you have made any modifications, they will be lost. Do you wish to leave anyway?',
  confirmLabel: 'Leave anyway',
  cancelLabel: 'Stay here',
  type: 'Warning'
};

@Injectable({ providedIn: 'root' })
export class ConfirmationService {
  private loadingService = inject(LoadingPageService);
  private activeSubject: Subject<boolean> | null = null;

  // State for the confirmation dialog component to consume
  readonly isOpen = signal(false);
  readonly options = signal<ConfirmOptions | null>(null);
  readonly loading = signal(false);

  setLoading(value: boolean): void {
    this.loading.set(value);
  }

  confirm(template: ConfirmOptions | 'dialog' | 'page'): Promise<boolean> {
    // Stop any pending page skeleton from showing
    this.loadingService.stopLoader();

    if (this.activeSubject) {
      this.activeSubject.next(false);
      this.activeSubject.complete();
    }

    const subject = new Subject<boolean>();
    this.activeSubject = subject;

    let options: ConfirmOptions | undefined = undefined;

    if (typeof template === 'string') {
      if (template === 'dialog') {
        options = dialogCloseOptions;
      }
      if (template === 'page') {
        options = pageCloseOptions;
      }
    } else {
      options = {
        title: 'Confirm action',
        confirmLabel: 'Confirm',
        cancelLabel: 'Cancel',
        type: 'Warning',
        ...template
      };
    }

    if (options === undefined) {
      throw new Error('Confirmation: No options sent');
    }

    this.options.set(options);

    this.isOpen.set(true);

    return firstValueFrom(subject);
  }

  handleChoice(choice: boolean): void {
    if (this.loading()) return;

    const subject = this.activeSubject;
    const keepOpen = choice && this.options()?.keepOpenOnConfirm;

    this.activeSubject = null;

    if (!keepOpen) {
      this.isOpen.set(false);
      this.loading.set(false);
    }

    if (subject) {
      subject.next(choice);
      subject.complete();
    }
  }

  close(): void {
    this.isOpen.set(false);
    this.loading.set(false);
  }

  /**
   * Shows a confirmation dialog and, if confirmed, executes the given
   * Observable while showing a loading state on the confirm button.
   * The dialog closes automatically when the Observable completes or errors.
   *
   * @returns `{ confirmed: false }` if the user cancels,
   *          `{ confirmed: true, result }` on success,
   *          `{ confirmed: true, error }` on failure.
   */
  async confirmAndExecute<T>(
    options: ConfirmOptions,
    action: () => Observable<T>
  ): Promise<ConfirmAndExecuteResult<T>> {
    const confirmed = await this.confirm({
      ...options,
      keepOpenOnConfirm: true
    });

    if (!confirmed) return { confirmed: false };

    this.setLoading(true);

    return new Promise<ConfirmAndExecuteResult<T>>((resolve) => {
      action()
        .pipe(finalize(() => this.close()))
        .subscribe({
          next: (result) => resolve({ confirmed: true, result }),
          error: (error) => resolve({ confirmed: true, error })
        });
    });
  }
}
